import React, { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../auth/AuthContext';
import '../styles/index.css';

const pages = [
  { to: '/', icon: '📊', label: 'Tableau de bord', end: true },
  { to: '/couts', icon: '💰', label: 'Coûts fournisseurs' },
  { to: '/couts-structure', icon: '🏢', label: 'Coûts de structure' },
  { to: '/clients', icon: '👤', label: 'Clients' },
  { to: '/devis', icon: '📄', label: 'Devis' },
  { to: '/factures', icon: '📝', label: 'Factures' },
  { to: '/tva', icon: '🧾', label: 'TVA' },
  { to: '/regroupements', icon: '🔀', label: 'Regroupements' },
];

const SIDEBAR_KEY = 'av2l.sidebar';
const MOBILE_BREAKPOINT = '(max-width: 899px)';

const Layout = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const isDark = theme === 'dark';

  /* Réduite en « format icônes » (desktop) — préférence mémorisée */
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
    } catch {
      return false;
    }
  });

  /* Tiroir plein écran (mobile) */
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_BREAKPOINT).matches);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_BREAKPOINT);
    const onChange = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setMobileOpen(false); // retour desktop → ferme le tiroir
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const persistCollapsed = (next) => {
    setCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'expanded');
    } catch {
      /* stockage indisponible : on ignore */
    }
  };

  const handleToggle = () => {
    if (isMobile) {
      setMobileOpen((open) => !open);
    } else {
      persistCollapsed(!collapsed);
    }
  };

  const closeMobile = () => setMobileOpen(false);

  const collapseLabel = collapsed ? 'Étendre le menu' : 'Réduire en icônes';

  return (
    <div
      className={`app ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'sidebar-mobile-open' : ''}`}
    >
      <div className="sidebar-backdrop" onClick={closeMobile} aria-hidden="true" />

      <aside className="sidebar" id="appSidebar" aria-label="Navigation principale">
        <div className="sidebar-header">
          <NavLink to="/" end className="sidebar-brand" title="Manager — Accueil">
            <img
              className="sidebar-brand-logo"
              src={`${process.env.PUBLIC_URL}/av2l-logo.png`}
              alt="Logo AV2L"
            />
            <span className="sidebar-label">Manager</span>
          </NavLink>
          <button
            type="button"
            className="sidebar-close"
            onClick={closeMobile}
            aria-label="Fermer le menu"
            title="Fermer le menu"
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          {pages.map((p) => (
            <NavLink
              key={p.to}
              to={p.to}
              end={p.end}
              onClick={closeMobile}
              title={p.label}
              className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
            >
              <span className="sidebar-icon" aria-hidden="true">{p.icon}</span>
              <span className="sidebar-label">{p.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button
            type="button"
            className="topbar-toggle"
            onClick={handleToggle}
            aria-controls="appSidebar"
            aria-expanded={isMobile ? mobileOpen : !collapsed}
            aria-label={isMobile ? 'Ouvrir le menu' : collapseLabel}
            title={isMobile ? 'Menu' : collapseLabel}
          >
            <span aria-hidden="true">
              {isMobile ? (mobileOpen ? '✕' : '☰') : collapsed ? '»' : '«'}
            </span>
          </button>

          <div className="topbar-actions">
            {user && (
              <span className="navbar-user" title={`Connecté : ${user.username}`}>
                {user.username}
              </span>
            )}
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              aria-pressed={isDark}
              aria-label={isDark ? 'Passer en thème clair' : 'Passer en thème sombre'}
              title={isDark ? 'Thème clair' : 'Thème sombre'}
            >
              <span aria-hidden="true">{isDark ? '☀️' : '🌙'}</span>
              <span className="theme-toggle-label">{isDark ? 'Clair' : 'Sombre'}</span>
            </button>
            <button
              type="button"
              className="btn-logout"
              onClick={logout}
              aria-label="Se déconnecter"
              title="Se déconnecter"
            >
              <span aria-hidden="true">⏻</span>
              <span className="theme-toggle-label">Déconnexion</span>
            </button>
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
